import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Sparkles, Clock, Eye, AlertCircle, Stethoscope, 
    CheckCircle, Users, ArrowRight, Heart, Apple, 
    Calendar, Book, Star, PawPrint, Dog,
    Crown, Check, HeartPulse, Info, Shield
} from "lucide-react";
import { cn } from "@/lib/utils";
import BackButton from './BackButton';

const FeatureItem = ({ Icon, text, color = "amber" }) => (
    <div className={cn(
        "flex items-center gap-1.5 sm:gap-2",
        "py-1.5 px-2 sm:py-2 sm:px-2.5",
        "rounded-lg sm:rounded-xl",
        color === "amber" ? "bg-amber-50/30" : "bg-orange-50/30"
    )}>
        <Icon className={cn(
            "w-3.5 h-3.5 sm:w-4 sm:h-4",
            color === "amber" ? "text-amber-500" : "text-orange-500"
        )} strokeWidth={2} />
        <span className="text-xs sm:text-sm text-gray-600">{text}</span>
    </div>
);

const AnalysisCard = ({ type, isSelected, onSelect }) => (
    <motion.button
        whileTap={{ scale: 0.98 }}
        onClick={() => {
            onSelect(type);
            requestAnimationFrame(() => {
                const button = document.getElementById('continue-button');
                const container = document.querySelector('[role="dialog"]');
                if (button && container) {
                    const buttonRect = button.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    const scrollOffset = buttonRect.top - containerRect.top - (containerRect.height - buttonRect.height);
                    container.scrollBy({ top: scrollOffset, behavior: 'smooth' });
                }
            });
        }}
        className={cn(
            "group relative w-full transition-all duration-200",
            "p-3 sm:p-4",
            "rounded-xl sm:rounded-2xl",
            type === 'quick' ? (
                isSelected
                    ? "ring-2 ring-amber-400 bg-amber-50/10"
                    : "border border-gray-100 bg-white hover:border-amber-100"
            ) : (
                isSelected
                    ? "ring-2 ring-orange-400 bg-orange-50/10"
                    : "border border-gray-100 bg-white hover:border-orange-100"
            )
        )}
    >
        {/* Badge */}
        <div className={cn(
            "absolute top-2 sm:top-3",
            type === 'quick' ? "left-2 sm:left-3" : "right-2 sm:right-3"
        )}>
            {type === 'quick' ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-green-50 text-green-600 border border-green-100">
                    <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3" strokeWidth={2.5} />
                    Estándar
                </span>
            ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-gradient-to-r from-amber-500/90 to-orange-500/90 text-white">
                    <Crown className="w-2.5 h-2.5 sm:w-3 sm:h-3" strokeWidth={2.5} />
                    Premium
                </span>
            )}
        </div>

        <div className="mt-4 sm:mt-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-2 sm:mb-3">
                <div className="flex items-center gap-2 sm:gap-2.5">
                    <div className={cn(
                        "w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center",
                        type === 'quick' ? "bg-amber-100/50" : "bg-orange-100/50"
                    )}>
                        {type === 'quick' ? (
                            <HeartPulse className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" strokeWidth={2} />
                        ) : (
                            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" strokeWidth={2} />
                        )}
                    </div>
                    <div>
                        <h3 className="text-sm sm:text-base font-semibold text-gray-800">
                            {type === 'quick' ? "Análisis de Salud" : "Análisis Integral"}
                        </h3>
                        <div className="flex items-center gap-1 text-[10px] sm:text-xs text-blue-600">
                            <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" strokeWidth={2.5} />
                            {type === 'quick' ? "Análisis IA en 1 minuto" : "Análisis IA + Guías personalizadas"}
                        </div>
                    </div>
                </div>
                
                {isSelected ? (
                    <div className={cn(
                        "w-5 h-5 sm:w-6 sm:h-6 rounded-lg flex items-center justify-center",
                        type === 'quick' 
                            ? "bg-gradient-to-br from-amber-500 to-amber-400"
                            : "bg-gradient-to-br from-orange-500 to-orange-400"
                    )}>
                        <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" strokeWidth={2.5} />
                    </div>
                ) : (
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-lg border-2 border-gray-200" />
                )}
            </div>

            {/* Features */}
            <div className={cn(
                "grid gap-1 sm:gap-1.5",
                type === 'complete' && "grid-cols-2"
            )}>
                {type === 'quick' ? (
                    <>
                        <FeatureItem Icon={Eye} text="Evaluación general de salud" />
                        <FeatureItem Icon={AlertCircle} text="Detección de anomalías" />
                        <FeatureItem Icon={Stethoscope} text="Reporte de salud básico" />
                    </>
                ) : (
                    <>
                        <FeatureItem Icon={Heart} text="Análisis avanzado" color="orange" />
                        <FeatureItem Icon={Book} text="Guía de raza" color="orange" />
                        <FeatureItem Icon={Shield} text="Plan de cuidados" color="orange" />
                        <FeatureItem Icon={Apple} text="Guía nutricional" color="orange" />
                    </>
                )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-2 pt-2 sm:mt-3 border-t border-gray-100">
                <div className="flex items-center gap-1 sm:gap-1.5">
                    {type === 'quick' ? (
                        <>
                            <Users className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-500" strokeWidth={2} />
                            <span className="text-[10px] sm:text-xs text-gray-500">Chequeo preventivo</span>
                        </>
                    ) : (
                        <>
                            <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-amber-400" strokeWidth={2} />
                            <span className="text-[10px] sm:text-xs text-gray-500">Cuidado personalizado</span>
                        </>
                    )}
                </div>
                <motion.div
                    animate={isSelected ? { x: [0, 3, 0] } : {}}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="flex items-center gap-1"
                >
                    <span className="text-[10px] sm:text-xs font-medium" style={{ color: type === 'quick' ? '#f59e0b' : '#f97316' }}>
                        Seleccionar
                    </span>
                    <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5" style={{ color: type === 'quick' ? '#f59e0b' : '#f97316' }} strokeWidth={2} />
                </motion.div>
            </div>
        </div>
    </motion.button>
);

const AnalysisTypeView = ({
    handleBack,
    analysisType,
    setAnalysisType,
    petName = "",
    onSubmit,
}) => {
    useEffect(() => {
        if (!analysisType) {
            setAnalysisType('quick');
        }
    }, []);

    return (
        <div className="flex flex-col h-[85vh] bg-white">
            {/* Header Section - Even more compact on mobile */}
            <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-sm">
                <div className="relative p-2 sm:p-6 md:p-8 text-center border-b bg-gradient-to-r from-amber-100/50 via-amber-50/50 to-amber-100/50">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 sm:w-32 h-1 bg-gradient-to-r from-amber-300 to-amber-500 rounded-b-lg" />
                    
                    <BackButton onClick={handleBack} />

                    <div className="mx-auto mb-1.5 sm:mb-4 relative">
                        <motion.div
                            initial={{ rotate: 0 }}
                            animate={{ rotate: [0, 12, 0] }}
                            transition={{ duration: 2, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
                            className="w-10 h-10 sm:w-16 sm:h-16 md:w-20 md:h-20 mx-auto bg-gradient-to-br from-amber-400 to-amber-500 
                                     rounded-lg sm:rounded-2xl shadow-lg flex items-center justify-center overflow-hidden"
                        >
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                            <Sparkles className="w-5 h-5 sm:w-9 sm:h-9 md:w-12 md:h-12 text-white" strokeWidth={2} />
                        </motion.div>
                        <motion.div
                            animate={{ y: [0, -4, 0] }}
                            transition={{ duration: 2, repeat: Infinity }}
                            className="absolute -bottom-1 sm:-bottom-2 right-1/2 transform translate-x-6 sm:translate-x-8"
                        >
                            <PawPrint className="w-3 h-3 sm:w-5 sm:h-5 md:w-6 md:h-6 text-amber-600" strokeWidth={2} />
                        </motion.div>
                    </div>

                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-0.5 sm:space-y-1"
                    >
                        <h2 className="text-base sm:text-xl md:text-2xl lg:text-3xl font-bold text-amber-900">
                            {petName ? `¿Cómo analizamos a ${petName}?` : '¿Qué tipo de análisis prefieres?'}
                        </h2>
                        <p className="text-[10px] sm:text-sm text-amber-700 max-w-md mx-auto">
                            Elige el tipo de análisis que mejor se adapte a tus necesidades
                        </p>
                    </motion.div>
                </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto">
                <div className="p-1.5 sm:p-6 md:p-8 pb-24">
                    <div className="max-w-2xl mx-auto space-y-2 sm:space-y-3">
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 sm:gap-4"
                        >
                            <AnalysisCard
                                type="quick"
                                isSelected={analysisType === 'quick'}
                                onSelect={setAnalysisType}
                            />
                            <AnalysisCard
                                type="complete"
                                isSelected={analysisType === 'complete'}
                                onSelect={setAnalysisType}
                            />
                        </motion.div>

                        {/* Selection helper text */}
                        {analysisType && (
                            <motion.div 
                                initial={{ opacity: 0, y: 5 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="px-2 py-1 sm:px-3 sm:py-2 bg-amber-50/50 border border-amber-100/50 rounded-lg sm:rounded-xl flex items-center justify-center gap-1 sm:gap-1.5"
                            >
                                <Info className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-500" />
                                <p className="text-[10px] sm:text-xs text-amber-700">
                                    {analysisType === 'quick' 
                                        ? "Has seleccionado el análisis de salud para un chequeo preventivo" 
                                        : "Has seleccionado el análisis integral para un cuidado personalizado"}
                                </p>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>

            {/* Fixed Button Container */}
            <div 
                id="continue-button"
                className="sticky bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-t border-amber-100/50 shadow-[0_-8px_16px_-6px_rgba(0,0,0,0.1)]"
            >
                <div className="p-1.5 sm:p-3 max-w-2xl mx-auto">
                    <AnimatePresence mode="wait">
                        {analysisType && (
                            <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 20 }}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={onSubmit}
                                className={cn(
                                    "w-full p-2.5 sm:p-4 rounded-xl font-medium shadow-lg",
                                    "transition-all duration-300",
                                    "flex items-center justify-center gap-2",
                                    "relative overflow-hidden",
                                    "bg-gradient-to-r",
                                    analysisType === 'quick'
                                        ? "from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600"
                                        : "from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600",
                                    "text-white"
                                )}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 animate-shimmer" />
                                {analysisType === 'quick' ? (
                                    <Clock className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />
                                ) : (
                                    <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />
                                )}
                                <span className="text-sm sm:text-base font-semibold">
                                    Comenzar {analysisType === 'quick' ? 'Análisis de Salud' : 'Análisis Integral'}
                                </span>
                                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.5} />
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default AnalysisTypeView;